import React from 'react';
import { FormattedMessage } from 'react-intl';
import * as mobxReact from 'mobx-react';
import { SubWindowsHost } from '../global/sub-window/sub-windows-host';
import { AccountAdminVM, AccountAdminTab } from './account-admin.viewmodel';
import { ListLayout } from '../global/tables/table-list-layout';
import { PassportField } from '../_events/_manage-event/_edit-website/website-form-body/passport-field/passport-field';
import './account-admin.scss';
import { hasPermission } from '../../infrastructure/utils/has-permissions';
import { Permission } from '../../api/roles.dao';
import { EnablementStatus } from '../../constants/common.constants';
import { CONFIGURE_TEXT, LOADING } from '../../constants/common-labels.constants';
import {
    PASSPORT_REGISTRATION_APPROVAL_STATUS_TEXT,
    REGISTRATION_APPROVAL_LABEL_TEXT,
    ORDER_BOOKING_STATUS_TEXT
} from '../../constants/registration-approval.constants';
import { AccountAdminSettingsType } from '../../api/account-admin';
import {
    ACCOUNT_ADMIN_SECTION_TEXT,
    AUTOMATIC_DATA_ANONYMIZATION_TEXT,
    AUTOMATIC_DATA_STATUS_TEXT,
    COMPLIANCE_EMAIL_STATUS_TEXT,
    COMPLIANCE_EMAILS_TEXT,
    RECURRING_ZOOM_MEETINGS_STATUS_TEXT,
    RECURRING_ZOOM_MEETINGS_TEXT,
    SEGREGATED_DATA_ENCRYPTION_STATUS_TEXT,
    SEGREGATED_DATA_ENCRYPTION_TEXT,
    THIRD_PARTY_EVENT_REGISTRATION_STATUS_TEXT,
    THIRD_PARTY_EVENT_REGISTRATION_TEXT,
    ORDER_BOOKING_LABEL_TEXT,
    ACCOUNT_SUBDOMAIN_TEXT,
    ACCOUNT_SUBDOMAIN_STATUS_TEXT,
    URL_PARAMETER_REGISTRATION_TEXT,
    URL_PARAMETER_REGISTRATION_STATUS_TEXT,
    SECURITY_AND_COMPLIANCE_SECTION_TEXT,
    INTEGRATION_SETTINGS_SECTION_TEXT,
    FEATURES_SECTION_TEXT,
    ORDER_BOOKING_SECTION_TEXT,
    OE_PASSPORT_ATTENDEE_TEXT,
    OE_PASSPORT_ATTENDEE_STATUS_TEXT,
    AI_AGENT_ACCESS_TEXT,
    AI_AGENT_ACCESS_STATUS_TEXT,
} from './account-admin.text';
import { ManageAttributesNavigation } from './_attributes/shared/attributes-navigation';
import { ManageAttributes } from './_attributes/manage-attributes';

const SettingsTabContent = mobxReact.observer((props: { vm: AccountAdminVM }) => {
    const { vm } = props;
    const { intl, isLoading } = vm;

    const getEnablamentSuffix = (type: AccountAdminSettingsType): EnablementStatus => {
        return vm.getEntityByType(type)?.snapshotSuffix || EnablementStatus.DISABLED;
    };

    if (isLoading) {
        return (
            <>
                <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs account-admin-tabs'>
                    <ManageAttributesNavigation />
                </section>
                <div className='title-sm'>
                    <FormattedMessage
                        {...LOADING}
                        values={{
                            suffix: ''
                        }}
                    />
                </div>
            </>
        );
    }

    return (
        <>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs account-admin-tabs'>
                <ManageAttributesNavigation />
            </section>
            <div className='account-admin-settings-grid'>
                <div className='account-admin-settings-column'>
                    {/* SECURITY & COMPLIANCE Section */}
                    <div className='account-admin-settings-section'>
                        <h3 className='account-admin-section-header'>
                            <FormattedMessage {...SECURITY_AND_COMPLIANCE_SECTION_TEXT} />
                        </h3>
                        <PassportField
                            labelText={intl.formatMessage(SEGREGATED_DATA_ENCRYPTION_TEXT)}
                            rowText={intl.formatMessage(SEGREGATED_DATA_ENCRYPTION_STATUS_TEXT,
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.SEGREGATED_DATA_ENCRYPTION) }
                            )}
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.SEGREGATED_DATA_ENCRYPTION)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            disabled={!hasPermission(Permission.MANAGE_SEGREGATED_DATA_ENCRYPTION)}
                            className={getEnablamentSuffix(AccountAdminSettingsType.SEGREGATED_DATA_ENCRYPTION) === EnablementStatus.DISABLED ? 'status-disabled' : ''}
                        />
                        <PassportField
                            labelText={intl.formatMessage(AUTOMATIC_DATA_ANONYMIZATION_TEXT)}
                            rowText={intl.formatMessage(AUTOMATIC_DATA_STATUS_TEXT,
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.DATA_ANONYMIZATION) }
                            )}
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.DATA_ANONYMIZATION)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            disabled={!hasPermission(Permission.MANAGE_DATA_ANONYMIZATION)}
                            buttonDataTestId='configure-data-anonymization'
                            className={getEnablamentSuffix(AccountAdminSettingsType.DATA_ANONYMIZATION) === EnablementStatus.DISABLED ? 'status-disabled' : ''}
                        />
                        <PassportField
                            labelText={intl.formatMessage(COMPLIANCE_EMAILS_TEXT)}
                            rowText={intl.formatMessage(COMPLIANCE_EMAIL_STATUS_TEXT,
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.COMPLIANCE_EMAIL_CAPTURE) }
                            )}
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.COMPLIANCE_EMAIL_CAPTURE)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            disabled={!hasPermission(Permission.COMPLAINCE_EMAILS)}
                            buttonDataTestId='compliance-emails'
                            className={getEnablamentSuffix(AccountAdminSettingsType.COMPLIANCE_EMAIL_CAPTURE) === EnablementStatus.DISABLED ? 'status-disabled' : ''}
                        />
                        <PassportField
                            labelText={intl.formatMessage(ACCOUNT_SUBDOMAIN_TEXT)}
                            rowText={intl.formatMessage(ACCOUNT_SUBDOMAIN_STATUS_TEXT,
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.ACCOUNT_SUBDOMAIN) }
                            )}
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.ACCOUNT_SUBDOMAIN)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            disabled={!hasPermission(Permission.MANAGE_ACCOUNT_SUBDOMAIN)}
                            buttonDataTestId='configure-account-subdomain'
                            className={getEnablamentSuffix(AccountAdminSettingsType.ACCOUNT_SUBDOMAIN) === EnablementStatus.DISABLED ? 'status-disabled' : ''}
                        />
                        <PassportField
                            labelText={intl.formatMessage(AI_AGENT_ACCESS_TEXT)}
                            rowText={intl.formatMessage(AI_AGENT_ACCESS_STATUS_TEXT,
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.AI_AGENT_ACCESS) }
                            )}
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.AI_AGENT_ACCESS)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            disabled={!hasPermission(Permission.MANAGE_AI_AGENT_ACCESS)}
                            buttonDataTestId='configure-ai-agent-access'
                            className={getEnablamentSuffix(AccountAdminSettingsType.AI_AGENT_ACCESS) === EnablementStatus.DISABLED ? 'status-disabled' : ''}
                        />
                    </div>

                    {/* INTEGRATION SETTINGS Section */}
                    <div className='account-admin-settings-section'>
                        <h3 className='account-admin-section-header'>
                            <FormattedMessage {...INTEGRATION_SETTINGS_SECTION_TEXT} />
                        </h3>
                        <PassportField
                            labelText={intl.formatMessage(THIRD_PARTY_EVENT_REGISTRATION_TEXT)}
                            rowText={intl.formatMessage(THIRD_PARTY_EVENT_REGISTRATION_STATUS_TEXT,
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.THIRD_PARTY_REGISTRATION) }
                            )}
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.THIRD_PARTY_REGISTRATION)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            className={`normal-label-text ${getEnablamentSuffix(AccountAdminSettingsType.THIRD_PARTY_REGISTRATION) === EnablementStatus.DISABLED ? 'status-disabled' : ''}`}
                            disabled={!hasPermission(Permission.MANAGE_THIRD_PARTY_REGISTRATION)}
                            buttonDataTestId='configure-third-party-registration'
                        />
                        <PassportField
                            labelText={intl.formatMessage(REGISTRATION_APPROVAL_LABEL_TEXT)}
                            rowText={intl.formatMessage({
                                ...PASSPORT_REGISTRATION_APPROVAL_STATUS_TEXT
                            },
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.MANAGE_REGISTRATION_APPROVAL) }
                            )}
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.MANAGE_REGISTRATION_APPROVAL)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            disabled={!hasPermission(Permission.MANAGE_REGISTRATION_APPROVAL_VIA_API)}
                            buttonDataTestId="configure-registration-approval"
                            className={getEnablamentSuffix(AccountAdminSettingsType.MANAGE_REGISTRATION_APPROVAL) === EnablementStatus.DISABLED ? 'status-disabled' : ''}
                        />
                        <PassportField
                            labelText={intl.formatMessage(URL_PARAMETER_REGISTRATION_TEXT)}
                            rowText={intl.formatMessage(URL_PARAMETER_REGISTRATION_STATUS_TEXT,
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.URL_PARAMETER_REGISTRATION) }
                            )}
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.URL_PARAMETER_REGISTRATION)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            disabled={!hasPermission(Permission.MANAGE_URL_PARAMETER_REGISTRATION)}
                            buttonDataTestId='url-parameter-registration'
                            className={getEnablamentSuffix(AccountAdminSettingsType.URL_PARAMETER_REGISTRATION) === EnablementStatus.DISABLED ? 'status-disabled' : ''}
                        />
                        <PassportField
                            labelText={intl.formatMessage(OE_PASSPORT_ATTENDEE_TEXT)}
                            rowText={intl.formatMessage(OE_PASSPORT_ATTENDEE_STATUS_TEXT,
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.OE_PASSPORT_ATTENDEE_SSO ) }
                            )}
                            buttonDataTestId='oe-passport-sso'
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.OE_PASSPORT_ATTENDEE_SSO)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            disabled={!hasPermission(Permission.MANAGE_SSO_CONFIGURATIONS)}
                            className={getEnablamentSuffix(AccountAdminSettingsType.OE_PASSPORT_ATTENDEE_SSO) === EnablementStatus.DISABLED ? 'status-disabled' : ''}
                        />
                    </div>
                </div>

                <div className='account-admin-settings-column'>
                    {/* FEATURES Section */}
                    <div className='account-admin-settings-section'>
                        <h3 className='account-admin-section-header'>
                            <FormattedMessage {...FEATURES_SECTION_TEXT} />
                        </h3>
                        <PassportField
                            labelText={intl.formatMessage(RECURRING_ZOOM_MEETINGS_TEXT)}
                            rowText={intl.formatMessage(RECURRING_ZOOM_MEETINGS_STATUS_TEXT,
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.RECURRING_ZOOM_MEETINGS) }
                            )}
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.RECURRING_ZOOM_MEETINGS)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            disabled={!hasPermission(Permission.CONFIGURE_RECURRING_ZOOM_MEETINGS)}
                            buttonDataTestId='configure-recurring-zoom-meetings'
                            className={getEnablamentSuffix(AccountAdminSettingsType.RECURRING_ZOOM_MEETINGS) === EnablementStatus.DISABLED ? 'status-disabled' : ''}
                        />
                    </div>

                    {/* ORDER BOOKING Section */}
                    <div className='account-admin-settings-section'>
                        <h3 className='account-admin-section-header'>
                            <FormattedMessage {...ORDER_BOOKING_SECTION_TEXT} />
                        </h3>
                        <PassportField
                            labelText={intl.formatMessage(ORDER_BOOKING_LABEL_TEXT)}
                            rowText={intl.formatMessage({
                                ...ORDER_BOOKING_STATUS_TEXT
                            },
                                { suffix: getEnablamentSuffix(AccountAdminSettingsType.MANAGE_ORDER_BOOKING) }
                            )}
                            onClick={() => vm.showAccountSettingForm(AccountAdminSettingsType.MANAGE_ORDER_BOOKING)}
                            buttonText={intl.formatMessage(CONFIGURE_TEXT)}
                            disabled={!hasPermission(Permission.MANAGE_ORDER_BOOKING)}
                            buttonDataTestId="configure-order-booking"
                            className={getEnablamentSuffix(AccountAdminSettingsType.MANAGE_ORDER_BOOKING) === EnablementStatus.DISABLED ? 'status-disabled' : ''}
                        />
                    </div>
                </div>
            </div>
        </>
    );
});

const AccountAdminPresentation = mobxReact.observer((props: { vm: AccountAdminVM }) => {
    const { vm } = props;
    const { intl } = vm;
    const activeTab = vm.getActiveTab();

    return (
        <SubWindowsHost>
            <div className='page-container'>
                <section className='top-page-title event-tracker horizontal-row horizontal-row--with-bottom-margin'>
                    <h1 className='text-ellipsis'>{intl.formatMessage(ACCOUNT_ADMIN_SECTION_TEXT)}</h1>
                </section>
                <section className='accounts-list fixed-list-layout account-admin-header'>
                    <div>
                        <ListLayout />
                    </div>
                </section>
                <section className='website-container'>
                    {activeTab === AccountAdminTab.Settings && <SettingsTabContent vm={vm} />}
                    {activeTab === AccountAdminTab.EventAttributes && <ManageAttributes/>}
                </section>
            </div>
        </SubWindowsHost>
    );
});

export { AccountAdminPresentation };
